import {
    Button,
    Container,
    Flex,
    PasswordInput,
    Stack,
    Text,
    TextInput,
    Title,
    Anchor,
    Alert,
    Image,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import axios from "axios";
import { useLayoutEffect, useState } from "react";
import checkUserId from "../../utils/checkuserid";
import { IconUser, IconAlertCircle, IconLock, IconMail } from "@tabler/icons-react";
import { API_URL } from "../../services/url";

interface SignInResponse {
    success: boolean;
    response: string;
    user?: number;
}

export const UserSignin = () => {
    const isMobile = useMediaQuery("(max-width: 600px)");

    const [email, setEmail] = useState("");

    const [password, setPassword] = useState("");
    const [passwordErr, setPasswordErr] = useState(false);

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const user = localStorage.getItem('user');

    const isFormValid = email && password && !passwordErr && !loading && !error;

    useLayoutEffect(() => {
        if (user) {
            window.location.href = ('/book')
        } else {
            setLoading(false)
        }
    }, []);

    const handleSignIn = async (e: any) => {
        e.preventDefault();
        if (isFormValid)
            try {
                setError("");
                setLoading(true);
                const response = await axios.post<SignInResponse>(`${API_URL}user/signin/`, {
                    email,
                    password,
                });

                if (response.data.success) {
                    localStorage.setItem("user", email);
                    window.location.href = "/book";
                    setLoading(false);
                } else {
                    setLoading(false);
                    setError(response.data.response);
                }
            } catch (error) {
                setLoading(false);
                console.error("Sign up failed", error);
                setError("An error occurred during sign up");
            }
    };

    const passwordPattern =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[\w!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/;

    const onBluePassword = (e: any) => {
        if (!passwordPattern.test(password)) {
            setError(
                "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character"
            );
            setPasswordErr(true);
        } else {
            setError("");
            setPasswordErr(false);
        }
    };

    return (
        <Container
            size={isMobile ? "xs" : "sm"}
            px={isMobile ? "md" : "xl"}
            py={isMobile ? "xl" : "80px"}
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
            }}
        >
            <Stack gap="xl" align="center" w="100%">
                {/* Header */}
                <Stack align="center" gap="md" ta="center">
                    <Image 
                        src="/landingimg.png" 
                        maw={isMobile ? 120 : 150} 
                    />
                    <Stack gap="xs" align="center">
                        <Title order={1} size={isMobile ? "h2" : "h1"} fw={700}>
                            Kabbie
                        </Title>
                        <Text size="lg" c="dimmed">
                            Passenger Portal
                        </Text>
                    </Stack>
                </Stack>

                {/* Form */}
                <Stack gap="lg" w="100%" maw={400}>
                    <Stack gap="xs" ta="center">
                        <Title order={2} size="h3">
                            Book Your Ride
                        </Title>
                        <Text size="sm" c="dimmed">
                            Sign in to book rides and track your trips
                        </Text>
                    </Stack>
                            <TextInput
                                label="Email Address"
                                placeholder="passenger@example.com"
                                type="email"
                                size="md"
                                required
                                leftSection={<IconMail size={18} />}
                                value={email}
                                onChange={(e) => setEmail(e.target.value.toLocaleLowerCase())}
                                onFocusCapture={(e) => setError("")}
                                styles={{
                                    label: { marginBottom: "0.5rem" },
                                }}
                            />

                            <PasswordInput
                                label="Password"
                                placeholder="Enter your password"
                                required
                                size="md"
                                leftSection={<IconLock size={18} />}
                                value={password}
                                error={passwordErr}
                                onChange={(e) => setPassword(e.target.value)}
                                onFocusCapture={(e) => setError("")}
                                onBlurCapture={onBluePassword}
                                styles={{
                                    label: { marginBottom: "0.5rem" },
                                }}
                            />

                            {error && (
                                <Alert
                                    icon={<IconAlertCircle size={16} />}
                                    color="red"
                                    variant="light"
                                    radius="md"
                                >
                                    <Text size="sm">{error}</Text>
                                </Alert>
                            )}

                            <Flex justify="flex-end" mt="xs">
                                <Anchor href="/forgot-password" size="sm" c="dimmed">
                                    Forgot Password?
                                </Anchor>
                            </Flex>

                    <Button
                        fullWidth
                        size="lg"
                        mt="md"
                        variant="gradient"
                        gradient={{ from: '#000000', to: '#333333', deg: 145 }}
                        onClick={handleSignIn}
                        radius="md"
                        loading={loading}
                        loaderProps={{ type: "oval" }}
                        disabled={!isFormValid}
                    >
                        Sign In
                    </Button>

                    <Stack gap="sm" mt="md" ta="center">
                        <Anchor href="/" size="sm" c="dimmed">
                            ← Back to Home
                        </Anchor>
                        <Text size="sm" c="dimmed">
                            New passenger?{" "}
                            <Anchor href="/user/signup" fw={600}>
                                Create Account
                            </Anchor>
                        </Text>
                    </Stack>
                </Stack>
            </Stack>
        </Container>
    )
}
